import { Article } from '../types';

export const powerGenerationArticles: Article[] = [
    {
        id: 'national-grid-collapse-analysis-2025',
        slug: 'national-grid-collapse-analysis-2025',
        title: 'National Grid Collapse Today: Complete Technical Analysis',
        excerpt: 'In-depth investigation of the recurring national grid collapses, examining TCN infrastructure failures, generation shortfalls, SCADA implementation delays, and practical solutions.',
        category: 'Power Generation',
        date: 'December 14, 2025',
        readTime: '4 min read',
        author: 'Energy Infrastructure Team',
        trending: false,
        keywords: ['national grid collapse today', 'TCN grid status', 'Nigeria electricity crisis', 'SCADA system'],
        metaDescription: 'Why Nigeria\'s grid keeps collapsing: technical analysis of TCN infrastructure, SCADA failures, and solutions for 2025.',
        content: `The national grid has experienced multiple collapses in 2025, continuing the troubling pattern from 2024. The first major collapse of 2025 occurred on February 12, when the Transmission Company of Nigeria (TCN) reported a complete system disruption affecting all 36 states. This comprehensive analysis explains the technical causes, regulatory failures, and practical solutions.

## Understanding Recent Collapses

Nigeria's grid has collapsed at least 3 times in 2025 (February 12, March 7, and September 10). During a typical collapse, generation drops from 4,200 MW to near-zero MW within 90 seconds—a cascading failure triggered by frequency instability.

### Immediate Impact (During Typical Grid Collapse)
- **226 million Nigerians** without grid power
- **All 11 DisCos** receiving zero allocation
- **Recovery timeline**: 10-16 hours for full restoration

## The Technical Root Causes

### 1. Aging Transmission Infrastructure
Nigeria's transmission backbone operates equipment designed 40+ years ago:
- **7,125 km** of 330kV transmission lines (67% over 30 years old)
- **Wheeling capacity**: 5,200 MW max (far below 12,910 MW installed generation)

### 2. The SCADA Implementation Failure
The World Bank-funded **Supervisory Control and Data Acquisition (SCADA)** system remains only 47% complete after 5 years. Without full SCADA, operators rely on manual phone coordination, making it impossible to arrest rapid frequency drops.

### 3. Generation-Transmission Mismatch
Nigeria generates 4,000-4,500 MW daily but transmission can only wheel 5,200 MW maximum. This creates a dangerous equilibrium where losing a single major generator (like Egbin's 1,320 MW) triggers system-wide collapse.

## What To Do During Grid Collapse

1. **Unplug sensitive electronics** to protect against surges when power returns.
2. **Check power.ng/grid-status** for official TCN updates.
3. **Preserve generator fuel** as blackouts often last 12+ hours.
4. **Band A users:** Document outage hours for future billing dispute claims.

## Conclusion
The national grid will continue collapsing until structural issues—aging infrastructure, SCADA delays, and financial liquidity—are addressed. Consumers must invest in backup solutions like solar to ensure business continuity.

## Frequently Asked Questions

**Why does Nigeria's national grid keep collapsing?**
The grid collapses due to three main reasons: (1) Aging transmission infrastructure over 40 years old that can only wheel 5,200 MW despite having 12,910 MW generation capacity, (2) Incomplete SCADA system preventing real-time monitoring and rapid response, and (3) Gas supply shortages limiting power plant output.

**How long does it take to restore power after a grid collapse?**
Full restoration typically takes 10-16 hours, though some areas may receive power within 4-6 hours depending on proximity to generation sources. Band A customers usually get priority restoration.

**What should I do when the grid collapses?**
Immediately unplug sensitive electronics to prevent surge damage when power returns, check power.ng for official TCN updates on restoration timeline, and preserve generator fuel as outages often exceed 12 hours. Band A customers should document outage hours for potential billing disputes.

**How many times has the grid collapsed in 2025?**
As of December 2025, the national grid has collapsed at least 3 times in 2025 (February 12, March 7, and September 10), continuing the pattern of instability from 2024 which recorded 12 collapses.

**Can the SCADA system prevent future grid collapses?**
Full SCADA implementation would significantly reduce collapses by enabling real-time monitoring and automated response to frequency drops. However, at 47% completion after 5 years, the World Bank-funded system still relies on manual phone coordination, making rapid intervention impossible.

## Related Articles
- [Band A Tariff 2025: Understanding Your Rights During Outages](/articles/band-a-electricity-tariff-guide-2025)
- [How to Report Your DisCo to NERC](/articles/how-to-file-nerc-complaint)
- [Generator vs Solar Cost Analysis](/articles/generator-vs-solar-cost-analysis)
- [Calculate Your Outage Costs](/tools/outage-calculator)`
    },
    {
        id: 'ikeja-electric-customer-care-guide',
        slug: 'ikeja-electric-customer-care-complete-contact-guide',
        title: 'Ikeja Electric Customer Care: Complete Contact Guide 2025',
        excerpt: 'All Ikeja Electric (IKEDC) contact numbers, WhatsApp lines, email addresses, and office locations. Learn how to report faults, check bills, and buy tokens.',
        category: 'Power Generation',
        date: 'January 15, 2025',
        readTime: '4 min read',
        author: 'Consumer Advocacy Team',
        trending: false,
        keywords: ['Ikeja Electric customer care', 'IKEDC contact number', 'Ikeja Electric WhatsApp', 'report transformer fault'],
        metaDescription: 'Complete contact directory for Ikeja Electric: customer care numbers, WhatsApp support, office locations, and fault reporting guide.',
        content: `Ikeja Electric (IKEDC) serves over 1.2 million customers across Lagos. If you need to report a fault, check your bill, or file a complaint, use these official channels.

## Official Contact Channels

### Primary Customer Care
- **Line:** 0700 453 5328
- **Hours:** Mon-Fri 8am-6pm, Sat 9am-2pm
- **Services:** Report outages, request meters, billing queries.

### WhatsApp Support
- **Number:** +234 813 190 0000
- **Available:** 24/7 for emergency reporting (safety issues).
- **Tip:** Use this for fastest response on transformer faults.

### Email Channels
- **General:** [email protected]
- **Billing:** [email protected]
- **Faults:** [email protected]

## Business Unit Offices

**Ikeja Business Unit**
- Address: 24/25 Marina, Lagos Island
- Coverage: Ikeja GRA, Maryland, Allen Avenue

**Shomolu Business Unit**
- Address: 7 Bajulaiye Road, Shomolu
- Coverage: Shomolu, Bariga, Akoka

**Oshodi Business Unit**
- Address: Oshodi-Apapa Expressway
- Coverage: Oshodi, Isolo, Mafoluku

## Common Issues & Solutions

### 1. Transformer Faults
If your transformer blows or has a fuse cut:
1. Report via WhatsApp immediately with the transformer location.
2. Get a ticket number.
3. Visit the Business Unit if not resolved in 48 hours.

### 2. Meter Token Issues
If your token is rejected ("Used" or "Invalid"):
- Check if you have debt on the meter.
- Verify the meter number on the receipt.
- Contact customer care with the Error Code (e.g., Error 30).

## How to File a Formal Complaint
1. **Level 1:** Call/Email Customer Care. Get a Ticket ID.
2. **Level 2:** If unresolved after 3 days, visit the Business Unit Manager.
3. **Level 3:** Escalate to NERC Forum Office if unresolved after 14 days (see our NERC guide).`
    },
    {
        id: 'abuja-disco-customer-care-guide',
        slug: 'abuja-electricity-disco-customer-care-complete-guide',
        title: 'Abuja Electricity (AEDC) Customer Care Guide',
        excerpt: 'Complete AEDC contact directory for FCT, Niger, Kogi, and Nasarawa. Phone numbers, WhatsApp, and office locations.',
        category: 'Power Generation',
        date: 'December 5, 2024',
        readTime: '2 min read',
        author: 'Consumer Rights Team',
        trending: false,
        keywords: ['AEDC customer care', 'Abuja electricity contact', 'AEDC WhatsApp'],
        metaDescription: 'Contact guide for Abuja Electricity (AEDC). Phone numbers, WhatsApp, and office locations for FCT, Niger, Kogi, and Nasarawa customers.',
        content: `Abuja Electricity Distribution Company (AEDC) covers the Federal Capital Territory, Niger, Kogi, and Nasarawa states.

## Contact Channels

- **Call Center:** 0803 907 0070
- **WhatsApp:** 0815 214 1414
- **Email:** [email protected]
- **Website:** www.abujaelectricity.com

## Key Business Units (FCT)

**Wuse Zone 4 Office**
- Coverage: Wuse, Maitama, Central Area
- Services: Metering, Bill reconciliation

**Gwarinpa Office**
- Coverage: Gwarinpa, Jahi, Katampe
- Services: Fault reporting, New connection

## Metering & Billing
To apply for a meter, visit the MAP section on the AEDC website. For billing disputes, ensure you have your last 3 months of bills and a photograph of your current reading.`
    },
    {
        id: 'band-a-tariff-guide-2025',
        slug: 'band-a-electricity-tariff-guide-2025',
        title: 'Band A Tariff 2025: Rates, Hours, and Your Rights',
        excerpt: 'Everything you need to know about the Band A electricity tariff in Nigeria. Rates, required supply hours, and how to downgrade if under-served.',
        category: 'Power Generation',
        date: 'October 5, 2025',
        readTime: '4 min read',
        author: 'Tariff Analysis Team',
        trending: false,
        keywords: ['Band A tariff', 'electricity rates Nigeria', 'NERC bands'],
        metaDescription: 'Band A electricity tariff guide: Current rate of ₦225/kWh, entitlement to 20+ hours supply, and how to report non-compliance.',
        content: `The Nigerian Electricity Regulatory Commission (NERC) revised the tariff structure in 2024, creating distinct bands based on supply hours.

## Understanding the Tariff Bands

| Band | Min Daily Hours | NERC-Approved Rate (2025) |
|------|-----------------|---------------------------|
| **A**| 20+ hours | ₦219.70 - ₦241.45/kWh |
| **B**| 16-20 hours | ₦63/kWh (Estimate) |
| **C**| 12-16 hours | ₦50/kWh (Estimate) |
| **D**| 8-12 hours | ₦41/kWh (Estimate) |
| **E**| 4-8 hours | ₦32/kWh (Estimate) |

*Note: Band A rate approved by NERC effective January 2025. Rates for other bands are estimates and vary by DisCo.*

## Band A Rights & Obligations
Customers in Band A pay a premium rate (₦219.70 - ₦241.45/kWh as of January 2025) but are legally entitled to minimum 20 hours daily average supply.

**If you get less than 20 hours:**
1. Log supply hours for 7 days.
2. Submit a formal complaint to your DisCo.
3. If performance consistently fails for 30 days, NERC regulations mandate a downgrade to Band B.

## How to Check Your Band
Visit your DisCo's website and enter your account/meter number in the "Feeder Status" or "Tariff" section.

## Frequently Asked Questions

**What is the current Band A electricity tariff in Nigeria?**
As of January 2025, Band A customers pay ₦219.70 - ₦241.45 per kilowatt-hour (kWh) as approved by NERC. This premium rate entitles customers to a minimum of 20 hours daily electricity supply.

**How do I know if I'm in Band A?**
Check your DisCo's website by entering your meter or account number in the tariff checker tool. You can also verify by monitoring your daily supply hours — if you consistently receive 20+ hours of power daily, you should be in Band A.

**Can I downgrade from Band A if I don't get 20 hours of supply?**
Yes. If your DisCo fails to provide the minimum 20 hours daily average for 30 consecutive days, NERC regulations mandate a downgrade to Band B. Log your supply hours for 7 days, submit a formal complaint to your DisCo, and request a band review.

**What happens if my DisCo overcharges me on Band A tariff?**
File a complaint with your DisCo's customer care first (get a ticket number). If unresolved within 14 days, escalate to the NERC Forum Office with evidence of your actual supply hours and bills. The Forum has the power to order refunds and band corrections.

**Is Band A tariff the same across all DisCos?**
No. While NERC sets the approved range (₦219.70-₦241.45/kWh), individual DisCos may charge different rates within this range. Rates for Bands B-E also vary by DisCo and are subject to monthly adjustments based on inflation and exchange rates.

## Related Articles
- [National Grid Collapses: Why Your Band A Power Still Fails](/articles/national-grid-collapse-analysis-2025)
- [How to Apply for a Prepaid Meter](/articles/prepaid-meter-application-nigeria-guide)
- [Calculate Your Electricity Bill](/tools/bill-calculator)
- [How to Report Your DisCo to NERC](/articles/how-to-file-nerc-complaint)`
    },
    {
        id: 'eko-electricity-customer-care',
        slug: 'eko-electricity-customer-care-guide',
        title: 'Eko Electricity (EKEDC) Contact Guide',
        excerpt: 'Contact details for Eko Disco serving Lagos Island, Lekki, Ikoyi, and Ajah. Report faults and resolve billing issues.',
        category: 'Power Generation',
        date: 'February 28, 2025',
        readTime: '2 min read',
        author: 'Consumer Advocacy Team',
        trending: false,
        keywords: ['EKEDC customer care', 'Eko Disco contact', 'Lekki electricity'],
        metaDescription: 'Eko Electricity (EKEDC) customer care numbers for Lagos Island, Lekki, and Ajah. Report faults and billing issues.',
        content: `Eko Electricity Distribution Company (EKEDC) is responsible for power distribution in southern Lagos.

## Contact Details
- **Helpline:** 0700 123 5678
- **Fraud Hotline:** 0800 00 2233
- **Email:** [email protected]

## Key Service Areas
- **Islands:** Victoria Island, Ikoyi, Lagos Island
- **Lekki/Ajah:** Lekki Phase 1, Ibeju-Lekki, Epe
- **Mainland:** Surulere, Apapa, Yaba

## Reporting Faults in Lekki/Ajah
For fastest response in the Lekki axis, use the dedicated WhatsApp line +234 815 000 0000 or visit the Ikota Business Unit.`
    },
    {
        id: 'prepaid-meter-application-guide',
        slug: 'prepaid-meter-application-nigeria-guide',
        title: 'How to Apply for a Prepaid Meter in 2025',
        excerpt: 'Step-by-step guide to getting a prepaid meter via the MAP scheme. Prices, timelines, and application links for all DisCos.',
        category: 'Power Generation',
        date: 'March 8, 2025',
        readTime: '2 min read',
        author: 'Consumer Rights Team',
        trending: false,
        keywords: ['prepaid meter application', 'MAP scheme Nigeria', 'meter prices'],
        metaDescription: 'Guide to prepaid meter application in Nigeria under the MAP scheme. Current prices for single and three-phase meters.',
        content: `Applying for a prepaid meter is the best way to avoid estimated billing "crazy bills."

## The MAP Scheme
The Meter Asset Provider (MAP) scheme allows customers to pay for meters upfront to get them installed within 10 working days.

## Current Meter Prices (2025 Estimate)
- **Single Phase:** ~₦88,000
- **Three Phase:** ~₦154,000
*(Prices subject to NERC review)*

## Application Process
1. **KYC:** Visit your DisCo website and complete the KYC (Know Your Customer) form.
2. **Debt Clearance:** You must clear outstanding bills or agree to a repayment plan.
3. **Site Verification:** Guidelines check if your wiring is safe.
4. **Payment:** Pay only to the designated bank account generated on the portal. **Do not pay cash to staff.**
5. **Installation:** Meter should be installed within 10 days of payment.

## Links by DisCo
- **Ikeja:** map.ikejaelectric.com
- **Eko:** ekedp.com/meter
- **Abuja:** abujaelectricity.com/map`
    },
    {
        id: 'nerc-complaint-process',
        slug: 'how-to-file-nerc-complaint',
        title: 'How to Report Your DisCo to NERC',
        excerpt: 'DisCo ignoring your complaints? Here is how to escalate to the NERC Consumer Forum for free arbitration.',
        category: 'Power Generation',
        date: 'April 15, 2025',
        readTime: '2 min read',
        author: 'Legal Team',
        trending: false,
        keywords: ['NERC complaint', 'report disco', 'consumer forum'],
        metaDescription: 'Step-by-step guide to filing a complaint with NERC if your DisCo fails to resolve your electricity issues.',
        content: `If your Distribution Company (DisCo) fails to resolve your complaint within 15 working days, you have the right to escalate to the Nigerian Electricity Regulatory Commission (NERC).

## The Hierarchy of Complaints
1. **Customer Care Unit:** First point of contact (get a ticket number!).
2. **Business Unit Manager:** If unresolved, write to the manager.
3. **NERC Forum Office:** The independent body that acts as a court for electricity disputes.

## How to File at NERC Forum
- **Locate your Forum:** There is one in every major city.
- **Submit Form:** Fill NERC Form 003.
- **Evidence:** Attach bills, ticket numbers, and photos.
- **Hearing:** You will be invited to a hearing to present your case. The Forum's ruling is legally binding on the DisCo.

## Common Winnable Cases
- "Crazy" estimated billing (over-billing).
- Wrongful disconnection.
- Failure to provide meter after payment.`
    },
    {
        id: 'kano-ksco-contact-guide',
        slug: 'kano-electricity-kedco-contact',
        title: 'Kano Electricity (KEDCO) Contact Guide',
        excerpt: 'Customer care channels for Kano, Jigawa, and Katsina states. KEDCO support lines and office addresses.',
        category: 'Power Generation',
        date: 'May 10, 2025',
        readTime: '2 min read',
        author: 'Consumer Advocacy Team',
        trending: false,
        keywords: ['KEDCO contact', 'Kano electricity', 'KEDCO customer care'],
        metaDescription: 'KEDCO customer care guide for Kano, Jigawa, and Katsina. Phone numbers, email, and complaint resolution.',
        content: `KEDCO serves the largest geographical area including Kano, Jigawa, and Katsina states.

## Contact Channels
- **Call:** 0700 5555 7777
- **Email:** [email protected]
- **Head Office:** 1 Niger Street, Kano.

## Reporting Vandalism
KEDCO has a dedicated line for reporting transformer vandalism in your community. Protecting infrastructure helps improve supply stability.`
    },
    {
        id: 'port-harcourt-phed-contact',
        slug: 'port-harcourt-electricity-phed-contact',
        title: 'Port Harcourt Electricity (PHED) Contact Guide',
        excerpt: 'Support channels for Rivers, Bayelsa, Cross River, and Akwa Ibom. Contact PHED for supply issues.',
        category: 'Power Generation',
        date: 'June 5, 2025',
        readTime: '2 min read',
        author: 'Consumer Advocacy Team',
        trending: false,
        keywords: ['PHED contact', 'Port Harcourt electricity', 'PHED customer care'],
        metaDescription: 'PHED customer care numbers for Rivers, Bayelsa, Cross River, and Akwa Ibom. Report outages and billing issues.',
        content: `Port Harcourt Electricity Distribution Company (PHED) powers the "4 States of the South-South": Rivers, Bayelsa, Cross River, and Akwa Ibom.

## 24/7 Helpline
- **Call:** 0700 2255 7433
- **Feedback:** [email protected]

## Common Issues in Region
- **Voltage Profile:** Report low voltage issues immediately to prevent appliance damage.
- **Billing:** PHED actively promotes the "Pay As You Consume" prepaid platform to reduce estimated billing disputes.`
    },
    {
        id: 'enugu-eedc-contact-guide',
        slug: 'enugu-electricity-eedc-contact',
        title: 'Enugu Electricity (EEDC) Customer Care',
        excerpt: 'Contact guide for the South-East: Enugu, Abia, Imo, Anambra, and Ebonyi. EEDC support lines.',
        category: 'Power Generation',
        date: 'July 12, 2025',
        readTime: '2 min read',
        author: 'Consumer Advocacy Team',
        trending: false,
        keywords: ['EEDC contact', 'Enugu electricity', 'EEDC customer care'],
        metaDescription: 'Customer care guide for EEDC serving the South-East states. Phone numbers and email support.',
        content: `Enugu Electricity Distribution Company (EEDC) serves the entire South-East geopolitical zone.

## Call Center
- **Hotline:** 084 700 100
- **Email:** [email protected]
- **WhatsApp:** 0815 082 5065

## "Feeder-by-Feeder" Updates
EEDC often publishes power availability status by feeder on their Facebook page. This is the most reliable way to check if a blackout is general or specific to your transformer.`
    }
];
